// Styling options for vector layers

import { useQueries } from "@tanstack/react-query";

import { getFileDescriptorViewQuery } from "@/queries/query-file-descriptors";
import { getPublicationFileVectorStyleQuery } from "@/queries/query-style-sprite";
import { VectorFileDescriptor } from "@/types/descriptor-types";
import {
  VectorDescriptorLayer,
  getVectorDescriptorLayers,
} from "@/utils/get-vector-descriptor-layers-and-sprite";

import { VectorLayer } from "../../../types/publication";

type FileDescriptorLayerPair = {
  fileDescriptor: VectorFileDescriptor;
  layer: VectorLayer;
};

// This function is used to get all the info needed to visualize all vector layers in the publication on the map.
// It does need to do a weird deduplication to avoid having duplicate queries when there are multiple layers with the same descriptor (same file).
// Then after that it combines the results of the queries to return an array of VectorDescriptorLayer,
// which contains all the info needed to visualize the layer on the map (style layers, url, type, etc).

export function usePublicationVectorStylingOptions(
  items: FileDescriptorLayerPair[]
): VectorDescriptorLayer[] | null {
  // Deduplicate to avoid redundant network requests for the same descriptor/style
  const uniqueDescriptors = items.filter(
    ({ fileDescriptor }, idx, arr) =>
      arr.findIndex((item) => item.fileDescriptor.id === fileDescriptor.id) === idx
  );
  const uniqueStyles = items.filter(
    ({ fileDescriptor, layer }, idx, arr) =>
      arr.findIndex(
        (item) =>
          item.fileDescriptor.id === fileDescriptor.id &&
          item.layer.properties.styleId === layer.properties.styleId
      ) === idx
  );

  const vectorStyleQueries = useQueries({
    queries: uniqueStyles.map(({ fileDescriptor, layer }) =>
      getPublicationFileVectorStyleQuery(fileDescriptor.id, layer.properties.styleId)
    ),
  });

  const fileDescriptorViewQueries = useQueries({
    queries: uniqueDescriptors.map(({ fileDescriptor }) =>
      getFileDescriptorViewQuery(fileDescriptor, "pmtiles")
    ),
  });

  if (
    vectorStyleQueries.some((q) => q.isFetching) ||
    fileDescriptorViewQueries.some((q) => !q.isSuccess)
  ) {
    return null;
  }

  return items.flatMap(({ fileDescriptor, layer }) => {
    const descriptorIdx = uniqueDescriptors.findIndex(
      (item) => item.fileDescriptor.id === fileDescriptor.id
    );
    const styleIdx = uniqueStyles.findIndex(
      (item) =>
        item.fileDescriptor.id === fileDescriptor.id &&
        item.layer.properties.styleId === layer.properties.styleId
    );

    const descriptorView = fileDescriptorViewQueries[descriptorIdx].data;
    if (!descriptorView) return [];

    const styleLayers = getVectorDescriptorLayers({
      descriptorId: fileDescriptor.id,
      meta: fileDescriptor.meta,
      styleJson: vectorStyleQueries[styleIdx].data,
      descriptorView,
      styleId: layer.properties.styleId,
    });

    return layer.layerInFile
      ? styleLayers.filter(({ layer: layerId }) => layerId === layer.layerInFile)
      : styleLayers;
  });
}
